// frontend/src/hooks/useShareLinks.ts
/**
 * React Query hooks for the owner's share links (design 8.4, 8.6). Keys are in
 * shareLinkKeys.ts (never persisted: each URL holds its token). Every change refreshes
 * the links and the photos whose share_link_count follows the links: the tank's
 * shots, and its organisms' photos (compare index and timeline; livestock
 * compare plan 7.5).
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { shareLinksApi } from '@/api/shareLinks';
import { livestockPhotoKeys } from './livestockPhotoKeys';
import { shareLinkKeys } from './shareLinkKeys';
import { tankPhotoKeys } from './tankPhotoKeys';
import type {
  RevokeAllResponse,
  ShareLink,
  ShareLinkCreate,
  ShareLinkCreated,
} from '@/types/shareLinks';

/**
 * The links, and the photos' share_link_count (tank shots, organism photos and
 * timelines). `refetchShots: false` only marks the photos stale: creating a
 * link from the compare page would otherwise refetch its photos (new signed
 * URLs) and reload them behind the modal. With `refetchShots`, only mounted
 * queries refetch: the open organism page's timeline reloads its loaded pages.
 */
export function invalidateShareLinks(
  queryClient: QueryClient,
  tankId?: string,
  { refetchShots = true }: { refetchShots?: boolean } = {}
): void {
  void queryClient.invalidateQueries({
    queryKey: tankId ? shareLinkKeys.tank(tankId) : shareLinkKeys.all,
  });
  const refetchType = refetchShots ? 'active' : 'none';
  void queryClient.invalidateQueries({
    queryKey: tankId ? tankPhotoKeys.all(tankId) : ['tank-photos'],
    refetchType,
  });
  void queryClient.invalidateQueries({
    queryKey: tankId ? livestockPhotoKeys.tank(tankId) : ['livestock-photos'],
    refetchType,
  });
  void queryClient.invalidateQueries({
    queryKey: tankId ? ['timeline', tankId] : ['timeline'],
    refetchType,
  });
}

/** The tank's live links, newest first. */
export function useShareLinks(tankId: string | undefined): UseQueryResult<ShareLink[], Error> {
  return useQuery({
    queryKey: shareLinkKeys.tank(tankId ?? ''),
    queryFn: () => shareLinksApi.list(tankId as string),
    enabled: !!tankId,
  });
}

/** Errors are the caller's to show: the share modal says them in place. */
export function useCreateShareLink(
  tankId: string
): UseMutationResult<ShareLinkCreated, Error, ShareLinkCreate> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: ShareLinkCreate) => shareLinksApi.create(tankId, body),
    onSuccess: () => invalidateShareLinks(queryClient, tankId, { refetchShots: false }),
  });
}

export function useRevokeShareLink(tankId: string): UseMutationResult<void, Error, number> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (linkId: number) => shareLinksApi.revoke(tankId, linkId),
    onSettled: () => invalidateShareLinks(queryClient, tankId),
  });
}

/** Every link on every tank of the account. */
export function useRevokeAllShareLinks(): UseMutationResult<RevokeAllResponse, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => shareLinksApi.revokeAll(),
    onSettled: () => invalidateShareLinks(queryClient),
  });
}
